import React, { useState } from 'react'
import { Row, Col, Image, Form, Button, InputGroup, Alert, Spinner } from 'react-bootstrap'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { FaEye, FaEyeSlash, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa'
import Card from '../../../components/Card'
import auth2 from '../../../assets/images/auth/02.png'
import { resetPassword } from '../../../services/authService'

const Recoverpw = () => {
  const navigate = useNavigate()
  const location = useLocation()

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [touched, setTouched] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // Extract token from URL (e.g., /reset-password?token=abcd123)
  const queryParams = new URLSearchParams(location.search)
  const token = queryParams.get('token')

  // Password validation: at least 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 special character
  const passwordValid = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?#&_])[A-Za-z\d@$!%*?#&_]{8,}$/.test(password)

  const passwordsMatch = password && confirmPassword && password === confirmPassword
  const showError = touched && confirmPassword && !passwordsMatch

  const handleReset = async () => {
    setTouched(true)
    setError('')
    setSuccess('')

    if (!password || !confirmPassword) {
      setError('Please fill out both password fields.')
      return
    }

    if (!passwordValid) {
      setError(
        'Password must be at least 8 characters long and include uppercase, lowercase, number, and special character.'
      )
      return
    }

    if (!passwordsMatch) {
      setError('Passwords do not match.')
      return
    }
    setTimeout(() => navigate('/'), 3000)
    try {
      setLoading(true)
      const response = await resetPassword(token, password)
      setSuccess(response.message || 'Password has been reset successfully!')
      setTimeout(() => navigate('/'), 3000)
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <section className="login-content">
        <Row className="m-0 align-items-center bg-white vh-100">
          <Col md="6" className="d-md-block d-none bg-primary p-0 mt-n1 vh-100 overflow-hidden">
            <Image src={auth2} className="img-fluid gradient-main animated-scaleX" alt="images" />
          </Col>

          <Col md="6" className="p-0">
            <Card className="card-transparent auth-card shadow-none d-flex justify-content-center mb-0">
              <Card.Body>
                <Link to="/dashboard" className="navbar-brand d-flex align-items-center mb-3">
                  <svg
                    width="30"
                    className="text-primary"
                    viewBox="0 0 30 30"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="-0.757324"
                      y="19.2427"
                      width="28"
                      height="4"
                      rx="2"
                      transform="rotate(-45 -0.757324 19.2427)"
                      fill="currentColor"
                    />
                    <rect
                      x="7.72803"
                      y="27.728"
                      width="28"
                      height="4"
                      rx="2"
                      transform="rotate(-45 7.72803 27.728)"
                      fill="currentColor"
                    />
                    <rect
                      x="10.5366"
                      y="16.3945"
                      width="16"
                      height="4"
                      rx="2"
                      transform="rotate(45 10.5366 16.3945)"
                      fill="currentColor"
                    />
                    <rect
                      x="10.5562"
                      y="-0.556152"
                      width="28"
                      height="4"
                      rx="2"
                      transform="rotate(45 10.5562 -0.556152)"
                      fill="currentColor"
                    />
                  </svg>
                  <h4 className="logo-title ms-3">Agrirent</h4>
                </Link>

                <h2 className="mb-2 fw-bold">Reset Password</h2>
                <p>Enter your new password to reset your account.</p>

                {/* Show success/error message */}
                {error && <Alert variant="danger">{error}</Alert>}
                {success && <Alert variant="success">{success}</Alert>}

                <Form>
                  <Row>
                    <Col lg="12">
                      {/* Password */}
                      <Form.Group className="floating-label mb-3 position-relative">
                        <Form.Label htmlFor="password">
                          Password <span className="text-danger">*</span>
                        </Form.Label>
                        <InputGroup>
                          <Form.Control
                            type={showPassword ? 'text' : 'password'}
                            id="password"
                            placeholder="Enter Password"
                            value={password}
                            onChange={(e) => {
                              setPassword(e.target.value);
                              setTouched(true);
                            }}
                            onBlur={() => setTouched(true)}
                            required
                          />
                          <Button
                            variant="outline-secondary"
                            onClick={() => setShowPassword(!showPassword)}
                            className="d-flex align-items-center"
                            style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                          >
                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                          </Button>
                        </InputGroup>

                       {/* Live feedback */}
                        {password && (
                          <div className="mt-1">
                            {passwordValid ? (
                              <span className="text-success d-flex align-items-center gap-1">
                                <FaCheckCircle /> Strong password
                              </span>
                            ) : (
                              <span className="text-danger d-flex align-items-center gap-1">
                                <FaExclamationCircle /> Weak password
                              </span>
                            )}
                          </div>
                        )}

                        {/* Password requirements */}
                        <small className="text-muted d-block mt-1">
                          Must include:
                          <ul className="mb-0 ps-3">
                            <li>At least 8 characters</li>
                            <li>One uppercase & one lowercase letter</li>
                            <li>One number</li>
                            <li>One special character (@$!%*?#&_)</li>
                          </ul>
                        </small>
                      </Form.Group>

                      {/* Confirm Password */}
                      <Form.Group className="floating-label mb-2 position-relative">
                        <Form.Label htmlFor="confirm-password">
                          Confirm Password <span className="text-danger">*</span>
                        </Form.Label>
                        <InputGroup>
                          <Form.Control
                            type={showConfirmPassword ? 'text' : 'password'}
                            id="confirm-password"
                            placeholder="Confirm Password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            onBlur={() => setTouched(true)}
                            required
                          />
                          <Button
                            variant="outline-secondary"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="d-flex align-items-center"
                            style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                          >
                            {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                          </Button>
                        </InputGroup>
                      </Form.Group>

                      {/* Live Validation Message */}
                      {confirmPassword && (
                        <div className="mt-1">
                          {showError ? (
                            <span className="text-danger d-flex align-items-center gap-1">
                              <FaExclamationCircle /> Passwords not match
                            </span>
                          ) : (
                            passwordsMatch && (
                              <span className="text-success d-flex align-items-center gap-1">
                                <FaCheckCircle /> Passwords match
                              </span>
                            )
                          )}
                        </div>
                      )}
                    </Col>
                  </Row>

                  <Button
                    className="mt-4 w-100"
                    type="button"
                    variant="primary"
                    onClick={handleReset}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" /> Resetting...
                      </>
                    ) : (
                      'Reset Password'
                    )}
                  </Button>
                </Form>
              </Card.Body>
            </Card>

            {/* Decorative background */}
            <div className="sign-bg sign-bg-right">
              <svg
                width="280"
                height="230"
                viewBox="0 0 431 398"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g opacity="0.05">
                  <rect
                    x="-157.085"
                    y="193.773"
                    width="543"
                    height="77.5714"
                    rx="38.7857"
                    transform="rotate(-45 -157.085 193.773)"
                    fill="#3B8AFF"
                  />
                  <rect
                    x="7.46875"
                    y="358.327"
                    width="543"
                    height="77.5714"
                    rx="38.7857"
                    transform="rotate(-45 7.46875 358.327)"
                    fill="#3B8AFF"
                  />
                  <rect
                    x="61.9355"
                    y="138.545"
                    width="310.286"
                    height="77.5714"
                    rx="38.7857"
                    transform="rotate(45 61.9355 138.545)"
                    fill="#3B8AFF"
                  />
                  <rect
                    x="62.3154"
                    y="-190.173"
                    width="543"
                    height="77.5714"
                    rx="38.7857"
                    transform="rotate(45 62.3154 -190.173)"
                    fill="#3B8AFF"
                  />
                </g>
              </svg>
            </div>
          </Col>
        </Row>
      </section>
    </>
  )
}

export default Recoverpw
